import { Directive, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HelpText } from '../../model/help-text';
import { FormControlEx } from '../forms/form-control';

export declare class ValueChangeEvent<TValue = any> {
  formSnapshot: any;
  newValue: TValue;
  previousValue: TValue;
}

const interactiveFieldDescendantSelector =
  'a[href], button, input, label, select, textarea, [contenteditable="true"], [role="button"], [role="checkbox"], [role="combobox"], [role="radio"]';

export function preventNonInteractiveHostPointerFocus(event: PointerEvent): void {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(interactiveFieldDescendantSelector)) {
    event.preventDefault();
  }
}

@Directive({})
export abstract class InputControlBase<TValue = any> implements OnInit, OnDestroy {
  constructor() {}

  private _control: AbstractControl<TValue, TValue> | null;
  @Input() set control(value: AbstractControl<TValue, TValue> | null) {
    this._control = value;
    this.onControlSet();
  }

  onControlSet() {
    if (!this.helpText && this.control?.helpText) this.helpText = this.control?.helpText;
  }

  get control(): FormControlEx<TValue> {
    return this._control as FormControlEx<TValue>;
  }

  @Input() helpText: string | HelpText;
  @Input() alignSelf: 'auto' | 'start' | 'center' | 'end' | 'stretch' | null = null;
  @Output() readonly valueChange: EventEmitter<ValueChangeEvent> = new EventEmitter();

  @HostBinding('style.align-self')
  get hostAlignSelf(): string | null {
    return this.alignSelf;
  }

  @HostListener('pointerdown', ['$event'])
  handleHostPointerDown(event: PointerEvent): void {
    preventNonInteractiveHostPointerFocus(event);
  }

  private _originalValue: TValue;
  protected readonly subscription: Subscription = new Subscription();

  protected getErrorMessage(): { message: string; params: any } {
    return this.errorMessage;
  }

  get errorMessage(): { message: string; params: any } {
    if (this.control?.invalid) {
      let error = this.control?.getErrorMessage();
      if (!error || !error.message) throw new Error('Error was not specified.');

      return error;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {}

  onblur() {
    if (this.control && this.control.parent && this.control.value !== this._originalValue) {
      this.valueChange.emit({
        formSnapshot: Object.assign({}, this.control.parent.value),
        newValue: this.control.value,
        previousValue: this._originalValue,
      });
    }
  }

  onfocus() {
    this._originalValue = this.control?.value;
  }
}
